import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import StripePaymentForm from '../../components/payment/StripePaymentForm';
import { Check, ArrowLeft, ShoppingCart, User, Mail } from 'lucide-react';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Get plan details from navigation state
  const planDetails = location?.state || {
    planName: 'Pro Plan',
    price: 29.99,
    interval: 'month'
  };

  // Guest user information state
  const [guestInfo, setGuestInfo] = useState({
    email: '',
    fullName: ''
  });
  const [guestInfoError, setGuestInfoError] = useState('');
  const [isGuestInfoValid, setIsGuestInfoValid] = useState(false);

  // Validate guest information
  useEffect(() => {
    if (user) {
      setIsGuestInfoValid(true);
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValid = 
        guestInfo?.email?.trim() !== '' && 
        emailRegex?.test(guestInfo?.email) &&
        guestInfo?.fullName?.trim() !== '';
      setIsGuestInfoValid(isValid);
      setGuestInfoError('');
    }
  }, [guestInfo, user]);

  const handleGuestInfoChange = (field, value) => {
    setGuestInfo(prev => ({ ...prev, [field]: value }));
  };

  const handlePaymentSuccess = (orderData) => {
    console.log('Payment successful:', orderData);
    
    // Navigate to order confirmation with payment intent or order ID
    const params = new URLSearchParams();
    if (orderData?.paymentIntentId) {
      params?.append('payment_intent', orderData?.paymentIntentId);
    }
    if (orderData?.orderNumber) {
      params?.append('order_number', orderData?.orderNumber);
    }
    
    navigate(`/order-confirmation?${params?.toString()}`);
  };

  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    
    // If Stripe fails, show option to use alternative payment methods
    const shouldShowAlternative = 
      error?.type === 'api_error' || 
      error?.code === 'service_unavailable'|| (error?.message && (error?.message?.includes('network') || error?.message?.includes('unavailable')));
    
    if (shouldShowAlternative) {
      // Show alternative payment option after a brief delay
      setTimeout(() => {
        const useAlternative = window.confirm(
          'Payment service is temporarily unavailable. Would you like to use an alternative payment method (Bank Transfer or Invoice)?'
        );
        if (useAlternative) {
          navigate('/alternative-payment-options', { state: planDetails });
        }
      }, 1000);
    }
  };

  const features = [
    'Unlimited projects',
    'Advanced AI features',
    'Priority support',
    'Custom domains',
    'Team collaboration',
    '99.9% uptime SLA'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Order Summary */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
              <div className="flex items-center mb-6">
                <ShoppingCart className="w-6 h-6 text-indigo-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Order Summary</h2>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{planDetails?.planName}</h3>
                    <p className="text-sm text-gray-500">Billed {planDetails?.interval}ly</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">
                      ${planDetails?.price}
                    </p>
                    <p className="text-sm text-gray-500">per {planDetails?.interval}</p>
                  </div>
                </div>

                <div className="space-y-3 pt-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Includes:</h4>
                  {features?.map((feature, index) => (
                    <div key={index} className="flex items-center text-gray-700">
                      <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 border-t border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold text-gray-900">${planDetails?.price}</span>
                </div>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-600">Tax (estimated)</span>
                  <span className="font-semibold text-gray-900">$0.00</span>
                </div>
                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <span className="text-lg font-bold text-gray-900">Total</span>
                  <span className="text-2xl font-bold text-indigo-600">${planDetails?.price}</span>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-100">
              <h3 className="font-semibold text-gray-900 mb-3">Secure Payment</h3>
              <p className="text-sm text-gray-600">
                Your payment information is encrypted and secure. We use industry-standard SSL encryption.
              </p>
            </div>
          </div>

          {/* Right Column - Payment Form */}
          <div className="space-y-6">
            {/* Guest Information Form (if not logged in) */}
            {!user && (
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                <div className="flex items-center mb-6">
                  <User className="w-6 h-6 text-indigo-600 mr-3" />
                  <h2 className="text-2xl font-bold text-gray-900">Your Information</h2>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        id="fullName"
                        type="text"
                        value={guestInfo?.fullName}
                        onChange={(e) => handleGuestInfoChange('fullName', e?.target?.value)}
                        placeholder="John Doe"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        id="email"
                        type="email"
                        value={guestInfo?.email}
                        onChange={(e) => handleGuestInfoChange('email', e?.target?.value)}
                        placeholder="john@example.com"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      We'll send your receipt and subscription details to this email
                    </p>
                  </div>

                  {guestInfoError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-600">{guestInfoError}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment Details</h2>
              
              {/* Alternative Payment Link */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 mb-2">
                  Having trouble with payment? Try our alternative payment options.
                </p>
                <button
                  onClick={() => navigate('/alternative-payment-options', { state: planDetails })}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 underline"
                >
                  View Alternative Payment Methods →
                </button>
              </div>

              {!user && !isGuestInfoValid ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    Please fill in your information above to continue with payment
                  </p>
                </div>
              ) : (
                <StripePaymentForm
                  amount={planDetails?.price}
                  planName={planDetails?.planName}
                  interval={planDetails?.interval}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  userEmail={user ? user?.email : guestInfo?.email}
                  userName={user ? user?.user_metadata?.full_name : guestInfo?.fullName}
                  userId={user ? user?.id : null}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;